import type { FC } from "react";
import { useEffect, useState } from "react";
import {
    HourGlassContainer,
    HourGlassImg,
    LoaderContainer,
    PixelPlaceholderLoaderStyled,
    SegmentPageResultsContainer,
    SegmentsUrlMainContainer,
    SegmentsUrlMainText,
    TooLongSegmentRulesErrorStateStyle,
} from "./segmentUrlResultSectionStyles";
import { SegmentUrlList } from "app/pages/segments/redesigned-wizard/segment-wizard/segments-url-list/SegmentUrlList";
import { AssetsService } from "app/services/AssetsService";
import { i18nFilter } from "app/filters/i18nFilter";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import type {
    ISegmentPageUrlResultsProps,
    ISegmentUrl,
} from "app/pages/segments/redesigned-wizard/segment-wizard/types";

const TIME_TO_SHOW_HOURGLASS = 3000;

export const SegmentResultsSection: FC<ISegmentPageUrlResultsProps> = (props) => {
    const {
        getUrlsPreview,
        selectedSite,
        isSelectedSiteAllowed,
        showTooLongSegmentRulesErrorText,
        percentageChangeFromLimitCharacters,
    } = props;
    const { segmentRules, segmentsPreviewLoading, segmentsReady } = useSegmentWizardContext();
    const [showLateResponseImage, setShowLateResponseImage] = useState(false);
    const [segmentUrls, setSegmentUrls] = useState<ISegmentUrl[]>();
    const [timeoutID, setTimeoutID] = useState(null);

    useEffect(() => {
        if (segmentsReady && selectedSite) {
            const getData = async () => {
                const urls = await getUrlsPreview(segmentRules);
                setSegmentUrls(urls);
            };
            getData();
        }
    }, [segmentRules, segmentsReady, selectedSite]);

    useEffect(() => {
        if (!selectedSite) {
            setShowLateResponseImage(false);
        } else if (segmentsPreviewLoading) {
            if (timeoutID) {
                clearTimeout(timeoutID);
            }
            setTimeoutID(setTimeout(() => setShowLateResponseImage(true), TIME_TO_SHOW_HOURGLASS));
            return () => {
                clearTimeout(timeoutID);
            };
        }
    }, [segmentsPreviewLoading, selectedSite]);

    const renderEmptyState = (isNoResultsState = false) => {
        const firstText = isNoResultsState
            ? "segment.wizard.redesigned.url.section.no.results.part1"
            : "segment.wizard.redesigned.url.section.empty.state";
        return (
            <SegmentsUrlMainContainer>
                {!isNoResultsState && <img src={AssetsService.assetUrl("/images/segments/url-empty-state.svg")} />}
                <SegmentsUrlMainText>{i18nFilter()(firstText)}</SegmentsUrlMainText>
                {isNoResultsState && (
                    <SegmentsUrlMainText>
                        {i18nFilter()("segment.wizard.redesigned.url.section.no.results.part2")}
                    </SegmentsUrlMainText>
                )}
            </SegmentsUrlMainContainer>
        );
    };

    const renderTooLongSegmentRulesErrorState = () => {
        return (
            <TooLongSegmentRulesErrorStateStyle>
                {i18nFilter()("segment.wizard.redesigned.url.section.too.long.segment.rules.error", {
                    percentageChange: percentageChangeFromLimitCharacters,
                })}
            </TooLongSegmentRulesErrorStateStyle>
        );
    };

    const renderHideUrlsPreview = () => {
        return (
            <SegmentsUrlMainContainer>
                <img src={AssetsService.assetUrl("/images/segments/affiliates-competitive-gap.svg")} />
                <SegmentsUrlMainText>
                    {i18nFilter()("segment.wizard.redesigned.url.section.hide.url.state.text")}
                </SegmentsUrlMainText>
            </SegmentsUrlMainContainer>
        );
    };

    const renderResultsUrlList = () => {
        const { selectedSite, rulesHighlightWords, rulesExactMatchHighlights, hideUrlsPreview } = props;

        return hideUrlsPreview ? (
            renderHideUrlsPreview()
        ) : (
            <SegmentUrlList
                selectedSite={selectedSite.name}
                urls={segmentUrls}
                wordHightlights={rulesHighlightWords}
                exactPhraseHighlights={rulesExactMatchHighlights}
                shouldShowFilter={true}
            />
        );
    };

    const urlLoader = () => {
        return (
            <LoaderContainer>
                <HourGlassContainer isVisible={showLateResponseImage}>
                    <HourGlassImg src={AssetsService.assetUrl("/images/segments/hour-glass-loader.svg")} />
                    <SegmentsUrlMainText>
                        {i18nFilter()("segment.wizard.redisgned.url.section.loader")}
                    </SegmentsUrlMainText>
                </HourGlassContainer>
                {Array(20)
                    .fill(null)
                    .map((item, idx) => (
                        <PixelPlaceholderLoaderStyled
                            key={idx}
                            width={296}
                            height={20}
                        />
                    ))}
            </LoaderContainer>
        );
    };

    const renderComponentContent = () => {
        if (showTooLongSegmentRulesErrorText) {
            return renderTooLongSegmentRulesErrorState();
        } else if (!selectedSite || !selectedSite?.validation?.isRobotsTxt || !isSelectedSiteAllowed) {
            return renderEmptyState();
        } else if (!segmentsPreviewLoading && segmentsReady) {
            if (segmentUrls?.length > 1) {
                return renderResultsUrlList();
            } else {
                //There are no url results for the selected filters
                return renderEmptyState(true);
            }
        } else {
            return urlLoader();
        }
    };

    return (
        <SegmentPageResultsContainer
            className={"page-results-container"}
            showTooLongSegmentRulesErrorText={showTooLongSegmentRulesErrorText}
        >
            {renderComponentContent()}
        </SegmentPageResultsContainer>
    );
};
